import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, PlugZap, CheckCircle2, XCircle } from "lucide-react";
import { LangToggle } from "../lib/publicLang";

const PLATFORMS = [
  { name: "MetaTrader 4", tagline: "The historical forex standard",
    strengths: ["Largest EA/indicator ecosystem (3000+ brokers)", "Proven 24/7 VPS automation"],
    gaps: ["No statistical validation (PBO, DSR, permutation)", "Dated UI, no vendor support", "MQL4 inaccessible to non-developers"],
    export: ["Terminal (Ctrl+T) → Account History", "Right-click → \"All History\"", "Right-click → \"Report\" (HTML) or CSV export — both formats accepted by ANATRADEX"] },
  { name: "MetaTrader 5", tagline: "The multi-asset successor",
    strengths: ["Multi-asset (stocks, indices, crypto)", "Richer multi-threaded backtests"],
    gaps: ["Still zero statistical robustness analysis", "Memory-heavy client-side backtests", "Ecosystem fragmentation with MT4"],
    export: ["Toolbox → History", "Right-click → \"Report\" → HTML or export", "Import the file as-is into ANATRADEX"] },
  { name: "cTrader", tagline: "Transparent ECN execution",
    strengths: ["Transparent ECN/STP execution", "Modern C#, more robust than MQL"],
    gaps: ["Automation requires real C# skills", "No built-in community", "No significance testing"],
    export: ["\"History\" tab at the bottom", "Right-click → \"Export to CSV\"", "Native CSV, directly compatible with ANATRADEX"] },
  { name: "TradingView", tagline: "Best charting on the market",
    strengths: ["Unmatched charting + accessible Pine Script", "Cloud, massive ideas community"],
    gaps: ["No native execution — real history lives at your broker", "Limiting freemium (alerts, multi-charts paywalled)", "Pine Script too limited for serious statistical validation"],
    export: ["Trade history lives at your connected broker (OANDA, IBKR…)", "Export the CSV from your broker portal", "ANATRADEX auto-maps standard columns"] },
];

export default function PlatformsGuideEn({ lang, setLang }) {
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="platforms-page-en">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div className="font-heading font-bold text-lg leading-none">ANATRADEX <span className="text-white/40 text-xs font-normal">/ Platforms</span></div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5"><ArrowLeft size={14} /> Back</Link>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </nav>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue mb-4">
          <PlugZap size={12} /> Works with your platform
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-4">
          Keep your platform.<br />Add statistical validation.
        </h1>
        <p className="text-white/60 text-lg mb-4 max-w-3xl leading-relaxed">
          MT4, MT5, cTrader and TradingView are excellent charting and execution tools. But <b className="text-white">none
          of them answers the only question that matters</b>: is your edge real, or were you lucky? ANATRADEX is the
          analysis layer that comes <em>after</em>: import your history, get PBO, Deflated Sharpe and permutation
          tests — reproducible to the exact digit.
        </p>
        <div className="text-xs text-white/40 mb-12">
          Zero code required · CSV/HTML import in 2 minutes · <Link to="/methodology" className="text-accent-blue underline">Public formulas</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <h2 className="font-heading text-xl font-bold">{p.name}</h2>
              <div className="text-xs text-white/50 mb-4">{p.tagline}</div>
              <div className="space-y-1.5 mb-3">
                {p.strengths.map((s) => (
                  <div key={s} className="flex items-start gap-2 text-sm text-white/70"><CheckCircle2 size={14} className="text-up shrink-0 mt-0.5" /> {s}</div>
                ))}
              </div>
              <div className="space-y-1.5 mb-4">
                {p.gaps.map((g) => (
                  <div key={g} className="flex items-start gap-2 text-sm text-white/50"><XCircle size={14} className="text-down shrink-0 mt-0.5" /> {g}</div>
                ))}
              </div>
              <div className="rounded-lg bg-black/30 border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-accent-blue mb-2">Export your history</div>
                <ol className="space-y-1 text-xs text-white/60 list-decimal pl-4">{p.export.map((e) => <li key={e}>{e}</li>)}</ol>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-up/30 bg-up/5 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold mb-3">Test it on your own trades — for free</h2>
          <p className="text-white/60 text-sm max-w-2xl mx-auto mb-6">
            The Free tier includes <b className="text-white">1 Statistical Analysis Report per month on your imported
            trades</b>. No demo to take on faith: your data, your numbers, our open methodology. Every report prints
            its seed; raw data is exportable.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/app/import" className="btn btn-primary text-base px-8 py-4">Import my trades <ArrowRight size={14} /></Link>
            <Link to="/methodology" className="btn text-base px-8 py-4">Read the methodology</Link>
          </div>
        </div>

        <p className="mt-8 text-[11px] text-white/35 leading-relaxed" data-testid="platforms-risk-disclaimer-en">
          ⚠️ ANATRADEX is a statistical analysis tool, not investment advice. ANATRADEX is not a regulated investment
          firm and is not registered with any financial authority (including the AMF, SEC/FINRA or FCA). Trading
          carries a risk of loss of up to 100% of invested capital — 70-85% of retail traders lose money (ESMA).
        </p>
      </div>
    </div>
  );
}
